import React, { memo, useCallback, useState } from "react";
import {
  AccordionDetails,
  AccordionSummary,
  IconButton,
  styled,
  Tooltip,
} from "@mui/material";
import { withStyles } from "@mui/styles";
import { featureTo2Lines } from "fe-common/logic";
import { QualifiedFeature, UserRoles } from "fe-common/graphql";
import { FindingStrength } from "../../util/FindingStrengthCalculator";
import { OrganSystemIcon } from "./organSystemIcon";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { KahunPrimaryTypography } from "fe-web-components/styles/typography";
import { AgentDiseaseFlags } from "./AgentDiseaseFlags";
import Button from "@mui/material/Button";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { RosebudColorMap } from "./rosebud/rosebudColorMap";
import { usePulse } from "../../util/usePule";
import { MobileView } from "../../util/aKaunGeneralUtil";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import Accordion from "@mui/material/Accordion";
import {
  DdFlagTypeExt,
  DiseaseCluster,
} from "fe-common/hooks/sortFeaturesWithCrs";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { MobileOnly } from "./util/mobileOnly";
import { DesktopOnly } from "./util/desktopOnly";
import MiniDiseaseView from "./miniDisease/miniDiseaseView";
import { Theme } from "@mui/material/styles";
import { SubTypesMenu } from "./miniDisease/subTypesMenu";
import { DDClinicalReasoning } from "fe-common/graphql/dao";
import { StarIcon } from "ask/src/components/diagnosticAgent/common/starIcon";
import { usePatientCase } from "./usePatientCase";
import { RoleRestrictor } from "fe-web-components/roles/RoleRestrictor";
import { StrengthIndicator } from "fe-web-components/patientCard/strengthIndicator";
//region [[ Styles ]]
type BooleanText = "true" | "false";
type AgentDiseaseItemViewType = {
  theme?: Theme;
  ismostlikely: BooleanText;
  dummy: BooleanText;
};

const AgentDiseaseItemView = styled(Accordion)(
  (props: AgentDiseaseItemViewType) => ({
    padding: "0",
    margin: "0",
    marginTop: "12px",
    borderRadius: "14px",
    "&:first-child, &:last-child": {
      marginTop: "12px !important",
      borderRadius: "14px",
    },
    backgroundColor:
      props.ismostlikely === "true" || props.dummy === "true"
        ? KahunColorMap.White
        : KahunColorMap.NewDisabledBackground,
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
    position: "relative",
    "&::before": {
      display: "none",
    },
    "& .MuiAccordion-root .Mui-expanded": {
      borderRadius: "14px",
    },
    [props.theme.breakpoints.down(MobileView)]: {
      boxShadow: "0px 1px 8px rgba(0, 0, 0, 0.12)",
      marginRight: "5px",
      marginLeft: "5px",
      borderRadius: "8px",
      "&:first-child, &:last-child": { borderRadius: "8px" },
    },
  }),
);

const DiseaseName = styled(KahunPrimaryTypography)(({ theme }) => ({
  fontSize: "17px",
  fontWeight: 500,
  color: KahunColorMap.DefaultLight,
  [theme.breakpoints.down(MobileView)]: {
    fontSize: "14px",
  },
}));

const AsterickAddedDisease = styled("span")(({ theme }) => ({
  fontSize: "21px",
  color: KahunColorMap.Secondary,
  [theme.breakpoints.down(MobileView)]: {
    fontSize: "18px",
  },
}));

const DiseaseNumOfCitations = styled(KahunPrimaryTypography)(({ theme }) => ({
  fontSize: "14px",
  [theme.breakpoints.down(MobileView)]: {
    fontSize: "13px",
  },
  fontWeight: 500,
  color: KahunColorMap.Accent,
}));

const TopContainer = withStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "row",
    paddingBottom: "6px",
    paddingTop: "4px",
    alignItems: "stretch",
    height: "70px",
    [theme.breakpoints.down(MobileView)]: {
      height: "70px",
      paddingBottom: "6px",
      paddingTop: "4px",
      paddingLeft: "8px",
      paddingRight: "0px",
    },
  },
  content: {
    margin: "12px 2px",
    borderBottom: "none",
    display: "flex",
    justifyContent: "space-between",
    "&$expanded": {
      borderBottom: "none",
    },
    borderRadius: "14px",
  },
  expanded: {},
}))(AccordionSummary);

const DiseaseNameCard = styled("div")({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  alignContent: "center",
  zIndex: 100,
  maxHeight: "36px",
  width: "100%",
});

const TitleContainer = styled("div")({
  display: "flex",
  flexFlow: "row",
  alignItems: "center",
});

const Title = styled("div")(({ theme }) => ({
  borderRadius: "10px",
  marginLeft: theme.spacing(1),
  paddingTop: "3px",
  "& .dummyText": {
    minWidth: "200px",
    minHeight: "30px",
  },
  [theme.breakpoints.down(MobileView)]: {
    marginLeft: theme.spacing(0.5),
  },
}));

const Flag = styled("div")({
  borderRadius: "10px",
  marginLeft: "5px",
});

const DiseaseActionButton = styled(IconButton)(({ theme }) => ({
  "&:focus": { outline: "none" },
  padding: "2px",
  height: "1.5em",
  width: "1.5em",
  [theme.breakpoints.down(MobileView)]: {
    height: "2em",
    width: "2em",
    "& svg": {
      height: "1.2em",
      width: "1.2em",
      fontSize: "1em",
    },
  },
}));

const ExpandIcon = styled(ExpandMoreIcon)(
  ({ expanded }: { expanded: string }) => ({
    transform: `rotate(${expanded === "true" ? "180" : "0"}deg)`,
    transition: "transform 0.3s ease-in",
  }),
);

const DiseaseActionsArea = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
});

const ExpandedContainer = styled(AccordionDetails)(
  ({ theme }: { theme?: Theme }) => ({
    display: "flex",
    flexFlow: "column",
    padding: "8px 0",
    backgroundColor: "white",
    borderRadius: "14px",
    [theme.breakpoints.up("sm")]: {
      padding: `${theme.spacing(2)}, ${theme.spacing(3)}`,
      boxShadow: `0 4px 16px 0 ${RosebudColorMap.uglyBlue4}`,
      height: "100%",
    },
  }),
);

const BottomButtonsArea = styled("div")({
  display: "flex",
  paddingLeft: "20px",
  paddingRight: "20px",
  width: "100%",

  justifyContent: "space-between",
});

const BottomActionButton = styled(Button)({
  margin: "10px",
  textTransform: "capitalize",
  height: "40px",
  borderRadius: "25px",
  "&:hover": {
    boxShadow: "none",
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface AgentDiseaseItemProps {
  fWithCrs: DiseaseCluster;
  className?: string;
  dummy?: boolean; // Used for pulse effect on dummy objects
  loading?: boolean;
  selected?: boolean;
  setSelection?: () => void;
  strength?: number;
  findingsStrength?: FindingStrength[];
  moreInfoBtnTitle?: string;
  onResourcesLinkClick?: (e: any) => void;
  onGraphLinkClick?: (diseaseId: string) => void;
  onExpand?: (isExpanded: boolean) => void;
  isExpanded?: boolean;
  onDeleteClick?: () => void;
  inPlan?: boolean;
  isMostLikely?: boolean;
  onShowDiseaseMenu?: (
    e: React.MouseEvent<HTMLButtonElement>,
    feature: QualifiedFeature,
  ) => void;
  index: number;
  sectionTitle: string;
}

//endregion [[ Props ]]

export const AgentDiseaseItem = memo(
  ({
    fWithCrs,
    strength,
    findingsStrength,
    isExpanded,
    loading,
    dummy,
    inPlan,
    isMostLikely,
    onShowDiseaseMenu,
    onExpand,
    onGraphLinkClick,
    onResourcesLinkClick,
    moreInfoBtnTitle,
    index,
    sectionTitle,
  }: AgentDiseaseItemProps) => {
    const feature = fWithCrs.qf;
    const flags = fWithCrs.flags;

    const [selectedCR, setSelectedCR] = useState<DDClinicalReasoning>();

    const onSubtypeSelect = (cr, subType) => {
      cr !== selectedCR ? setSelectedCR(cr) : setSelectedCR(null);
      evt({
        category: CATEGORY.AGENT_DISEASE_CARD,
        action:
          cr !== selectedCR ? ACTION.SELECT_SUBTYPE : ACTION.REMOVE_SUBTYPE,
        label: subType,
      });
    };

    const isLoading = loading || dummy;

    const { pulse, pulseStrength, withAnimation } = usePulse({
      disabled: !isLoading,
    });

    const [primaryFeatureName] = featureTo2Lines(feature);
    const citationsCount = fWithCrs.citations?.totalCount;
    const articlesFoundText =
      citationsCount > 0 ? `${citationsCount} related articles` : "";

    const { isPendingChange, applyPendingFindings } = usePatientCase();

    const updateDiagnosisClicked = useCallback(() => {
      evt({
        category: CATEGORY.PLAN,
        action: ACTION.CALCULATE,
      });
      applyPendingFindings();
    }, [applyPendingFindings]);

    const handleExpandEvent = (e, wasExpanded) => {
      if (!onExpand) return;

      const addedToPlan = flags.find((f) => f === "ADDED")
        ? " (Added To Plan)"
        : "";
      const removedToPlan = flags.find((f) => f === "REMOVED")
        ? " (Removed From Plan)"
        : "";

      const number = "#" + (index + 1) + " ";

      const label =
        number +
        primaryFeatureName +
        " (" +
        sectionTitle +
        ")" +
        addedToPlan +
        removedToPlan;

      console.log(label);

      onExpand(wasExpanded);

      evt({
        category: CATEGORY.AGENT_DD_LIST,
        action: wasExpanded ? ACTION.EXPAND_DISEASE : ACTION.COLLAPSE_DISEASE,
        label: label,
        fb: wasExpanded,
      });
    };

    const handleGraphClicked = (source: string) => () => {
      evt({
        category: CATEGORY.AGENT_DD_LIST,
        action: ACTION.CLICK_MORE_INFO,
        label: primaryFeatureName + source ? `-${source}` : "",
      });
      onGraphLinkClick(feature.id);
    };

    const handleResourcesClicked = (source: string) => () => {
      evt({
        category: CATEGORY.AGENT_DD_LIST,
        action: ACTION.OPEN_RESOURCES,
        label: primaryFeatureName + source ? `-${source}` : "",
      });
      onResourcesLinkClick(feature.id);
    };

    const addOrRemoveIndicator = useCallback(() => {
      const featureWasManlyAdded = flags?.some(
        (f) => f === DdFlagTypeExt.Added,
      );

      const featureWasManlyRemoved = flags?.some(
        (f) => f === DdFlagTypeExt.Removed,
      );

      if (!featureWasManlyAdded && !featureWasManlyRemoved) return null;

      const addedOrRemovedText = `This disease was manually
     ${featureWasManlyAdded ? "added to" : "removed from"} plan`;

      return (
        <Tooltip title={addedOrRemovedText}>
          <AsterickAddedDisease>*</AsterickAddedDisease>
        </Tooltip>
      );
    }, [flags]);

    // disable diagnose button for now
    const showDiagnoseButton = isPendingChange();

    return (
      <AgentDiseaseItemView
        expanded={!!isExpanded}
        onChange={handleExpandEvent}
        elevation={0}
        ismostlikely={isMostLikely ? "true" : "false"}
        dummy={dummy ? "true" : "false"}
        TransitionProps={{ unmountOnExit: true }}
      >
        <TopContainer>
          <DiseaseNameCard>
            <TitleContainer>
              <OrganSystemIcon
                feature={feature}
                showBackground={true}
                loading={dummy}
                className={`${isLoading ? pulse : ""} ${withAnimation}`}
              />
              <Title className={`${dummy ? pulse : ""} ${withAnimation}`}>
                <DiseaseName>
                  {dummy && <div className={"dummyText"}>&nbsp;</div>}
                  {primaryFeatureName !== "null" ? primaryFeatureName : ""}
                  {addOrRemoveIndicator()}
                </DiseaseName>
                {!isExpanded && articlesFoundText && (
                  <DiseaseNumOfCitations>
                    {articlesFoundText}
                  </DiseaseNumOfCitations>
                )}

                {isExpanded && (
                  <SubTypesMenu
                    subTypes={fWithCrs.crs}
                    selected={selectedCR}
                    onSelect={onSubtypeSelect}
                  />
                )}
              </Title>
              <Flag className={`${isLoading ? pulse : ""} ${withAnimation}`}>
                {feature.feature && <AgentDiseaseFlags flags={flags} />}
              </Flag>
            </TitleContainer>
            {!isLoading && (
              <DiseaseActionsArea>
                <StarIcon checked={inPlan} disease={feature} />

                <DiseaseActionButton size={"medium"}>
                  <MobileOnly>
                    <KeyboardArrowRightIcon />
                  </MobileOnly>
                  <DesktopOnly>
                    <ExpandIcon expanded={isExpanded ? "true" : "false"} />
                  </DesktopOnly>
                </DiseaseActionButton>
              </DiseaseActionsArea>
            )}
          </DiseaseNameCard>

          {!isExpanded && (
            <StrengthIndicator
              strength={strength}
              className={`${isLoading ? pulseStrength : ""} ${withAnimation}`}
            />
          )}
        </TopContainer>
        <ExpandedContainer>
          <MiniDiseaseView fWithCrs={fWithCrs} selectedSubType={selectedCR} />
          <BottomButtonsArea>
            {onGraphLinkClick && (
              <RoleRestrictor
                roles={[
                  UserRoles.KahunBackofficeUsers,
                  UserRoles.KahunPatientDemoUsers,
                ]}
              >
                <BottomActionButton
                  variant="outlined"
                  color={"primary"}
                  onClick={handleGraphClicked("expandedAction")}
                >
                  {moreInfoBtnTitle}
                </BottomActionButton>
              </RoleRestrictor>
            )}
            {onResourcesLinkClick && (
              <BottomActionButton
                variant="outlined"
                color={"primary"}
                onClick={handleResourcesClicked("expandedAction")}
              >
                Resources
              </BottomActionButton>
            )}

            {showDiagnoseButton && (
              <BottomActionButton
                variant="contained"
                color={"primary"}
                onClick={updateDiagnosisClicked}
              >
                Update
              </BottomActionButton>
            )}
          </BottomButtonsArea>

          {isExpanded && (
            <StrengthIndicator
              strength={strength}
              className={`${isLoading ? pulseStrength : ""} ${withAnimation}`}
            />
          )}
        </ExpandedContainer>
      </AgentDiseaseItemView>
    );
  },
);
